import { useKeenSlider } from "keen-slider/react";
import Image from "next/image";
import React from "react";
import { container_classname, sub_section_heading } from "../new-ui/constants";

export interface PaymentSectionDataProps {
    imageUrl: string;
    title?: string;
    description?: string;
}

interface PaymentProcessProps {
    paymentSectionData: PaymentSectionDataProps[];
    selectedIndex: number;
    previousIndex: number;
    opacities: number[];
    imageBackgroundColor?: string;
}

interface PaymentSectionProps {
    paymentSectionData?: PaymentSectionDataProps[];
    backgroundColor?: string;
    imageBackgroundColor?: string;
    title: string;
    description?: string;
}

const defaultPaymentSectionData: PaymentSectionDataProps[] = [
    {
        imageUrl: "/integration/tab1.png",
        title: "Go to Integrations tab",
        description: "Go to Integrations → Payments in the Gallabox Dashboard and select the payment gateway.",
    },
    {
        imageUrl: "/integration/tab2.png",

        title: "Name your Integration",
        description: "Name your integration and initiate the connection with your payment gateway.",
    },
    {
        imageUrl: "/integration/tab3.png",

        title: "Authorize Gallabox",
        description: "Permit Gallabox to connect with your preferred payment gateway.",
    },
    {
        imageUrl: "/integration/tab4.png",
        title: "Configure the settings",
        description: "Choose your preferred WhatsApp channel and configure it.",
    },
];

const Payment: React.FC<PaymentProcessProps> = (props) => {
    const { paymentSectionData, selectedIndex, previousIndex, opacities, imageBackgroundColor = "#B9D4FF" } = props;
    return (
        <div>
            {paymentSectionData.map((paymentSteps, idx) => {
                const { imageUrl } = paymentSteps;

                return (
                    <div
                        key={idx}
                        id={`slider-id-${idx}`}
                        className="fader__slide w-full lg:pb-12 pb-0"
                        style={{ opacity: selectedIndex === idx || previousIndex === idx ? opacities[idx] : 0 }}
                    >
                        <div className="w-full flex lg:gap-14 gap-0 lg:pt-0 pt-6">
                            <div className="hidden lg:block px-36 py-5" style={{ backgroundColor: imageBackgroundColor, borderRadius: "6px" }}>
                                <Image width={1240} height={427} src={imageUrl} alt={"steps to integrate"} />
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

const PaymentProcessCard: React.FC<PaymentSectionProps> = (props) => {
    const {
        paymentSectionData = defaultPaymentSectionData,
        backgroundColor = "var(--light-grad-1, linear-gradient(103deg, #EDF4FF 28.71%, #D2DDFF 84.45%, #84A2FF 136.43%))",
        imageBackgroundColor,
        title,
        description,
    } = props;
    const [opacities, setOpacities] = React.useState<number[]>([]);
    const [previousIndex, setPreviousIndex] = React.useState<number>(0);
    const [selectedIndex, setSelectedIndex] = React.useState<number>(0);
    const [randomNumber, setRandomNumber] = React.useState<number>(Math.random());

    const autoPlay = true;
    const autoPlayInterval = 7000;

    const [sliderRef, instanceRef] = useKeenSlider({
        initial: 0,
        slides: paymentSectionData.length,
        loop: true,
        drag: false,
        detailsChanged(slider) {
            const newOpacities = slider.track.details.slides.map((slide) => slide.portion);
            newOpacities.forEach((opacity, idx) => {
                const elements = document.querySelectorAll(`[id="slider-id-${idx}"]`);
                elements.forEach((elm) => {
                    if (!elm) return;
                    (elm as HTMLElement).style.opacity = String(opacity);
                });
            });
            setOpacities(newOpacities);
        },
    });

    const currentSlideIndex = instanceRef.current?.track?.details?.rel;

    const [thumbnailRef] = useKeenSlider(
        {
            initial: 0,
            slides: {
                perView: 4,
                spacing: 10,
            },
        },
        [
            (slider) => {
                slider.slides.forEach((slide, idx) => {
                    slide.addEventListener("click", () => {
                        if (instanceRef.current) {
                            if (typeof instanceRef.current?.track?.details?.rel === "number") {
                                setPreviousIndex(instanceRef.current?.track?.details?.rel);
                            }
                            instanceRef.current.moveToIdx(idx);
                            setSelectedIndex(idx);
                            setRandomNumber(Math.random());
                        }
                    });
                });
            },
        ]
    );

    const desktopLogos = paymentSectionData.map((testimony) => ({
        title: testimony.title,
        description: testimony.description,
    }));

    React.useEffect(() => {
        const interval = setInterval(() => {
            if (autoPlay && instanceRef.current) {
                instanceRef.current.next();
                setSelectedIndex((prev) => {
                    if (prev === paymentSectionData.length - 1) return 0;
                    return prev + 1;
                });
            }
        }, autoPlayInterval);
        return () => clearInterval(interval);
    }, [randomNumber, autoPlay, instanceRef, paymentSectionData.length]);

    return (
        <section
            className="lg:pt-20 lg:pb-10  pt-16 lg:block hidden"
            style={{
                background: backgroundColor,
            }}
        >
            <div className={`${container_classname} flex flex-col lg:gap-14 gap-10`}>
                <div>
                    <h2 className={`${sub_section_heading} mb-5`} style={{ fontFamily: "Degular Display" }}>
                        {title}
                    </h2>
                    {description && <p className="text-xl">{description}</p>}
                </div>

                <div className="w-full keen-slider thumbnail-slider" ref={thumbnailRef}>
                    {desktopLogos.map((logo, idx) => {
                        const customStyle = currentSlideIndex === idx ? "loading" : "inactive";
                        return (
                            <div key={idx} className="keen-slider__slide thumbnail-slider__slide cursor-pointer">
                                <div className={`box ${customStyle}`}>
                                    <div className={`box-border-top ${customStyle}`}></div>
                                </div>
                                <div className="w-full">
                                    <p className="mt-6 font-bold h-10">{logo.title}</p>
                                    <div className="w-5 h-5 rounded-full bg-blue-600 flex items-center justify-center font-semibold mt-2 text-white text-sm">{idx + 1}</div>
                                    <p className="mt-3">{logo.description}</p>
                                </div>
                            </div>
                        );
                    })}
                    <style jsx>
                        {`
                            .thumbnail-slider .thumbnail-slider__slide {
                                max-width: unset !important;
                                min-width: unset !important;
                            }
                        `}
                    </style>
                </div>

                <div ref={sliderRef} className="fader">
                    <Payment
                        paymentSectionData={paymentSectionData}
                        selectedIndex={selectedIndex}
                        previousIndex={previousIndex}
                        opacities={opacities}
                        imageBackgroundColor={imageBackgroundColor}
                    />
                </div>
            </div>
        </section>
    );
};

export default PaymentProcessCard;
